import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Layer } from '@carbon/react';
import { Draggable, TrashCan } from '@carbon/react/icons';

/**
 * The whole of what a drop does, kept pure because the drag itself cannot run in jsdom: @dnd-kit
 * needs layout and pointer geometry a headless DOM does not provide. `arrayMove` supplies the
 * same index arithmetic the sortable strategy assumes.
 */
export function moveItem<T>(items: T[], from: number, to: number): T[] {
  // Bounds-checked at BOTH ends, unlike `moveSection` in the Sections screen: dnd-kit can hand the
  // drop handler a stale index when the list changes mid-drag, and `arrayMove` splices `undefined`
  // in for a positive out-of-range `from` (the editor then throws reading `row.caption`) and
  // silently reorders for an out-of-range `to`. This is a deliberate divergence from `moveSection`;
  // the parked follow-up is to unify the two, not to copy this weakness back into this guard.
  if (from < 0 || to < 0 || from >= items.length || to >= items.length || from === to) {
    return items;
  }
  return arrayMove(items, from, to);
}

interface SortableRowProps {
  id: string;
  /** The remove button's accessible name, e.g. "Remove photograph". */
  removeLabel: string;
  onRemove: () => void;
  children: React.ReactNode;
}

/**
 * One draggable row, drawn as a card with its actions (move, remove) in the top-right corner. The
 * handle is a real button so the keyboard sensor can reach it, exactly as the Sections screen's
 * handle is. The `Layer` lifts the fields one level so they stand out from the card.
 */
export const SortableRow = React.memo(({ id, removeLabel, onRemove, children }: SortableRowProps) => {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  return (
    <li
      ref={setNodeRef}
      className={`storefront-extras__row${isDragging ? ' storefront-extras__row--dragging' : ''}`}
      style={{ transform: CSS.Translate.toString(transform), transition }}
    >
      <Layer className={'storefront-extras__fields'}>{children}</Layer>
      <div className={'storefront-extras__actions-bar'}>
        <button
          type="button"
          className={'storefront-extras__handle'}
          aria-label={t('storefront.extras.reorder')}
          {...attributes}
          {...listeners}
        >
          <Draggable size={16} />
        </button>
        <Button
          className={'storefront-extras__remove'}
          renderIcon={TrashCan}
          hasIconOnly
          kind={'ghost'}
          size={'sm'}
          iconDescription={removeLabel}
          tooltipPosition={'left'}
          onClick={onRemove}
        />
      </div>
    </li>
  );
});

SortableRow.displayName = 'SortableRow';

interface SortableListProps {
  /**
   * The row ids in display order. These lists have no server-side identity — the API stores
   * `{ fileId, caption }`, not `{ id, fileId, caption }` — so the display index IS the id. The
   * drop handler therefore works on positions, and the ids are recomputed on every render.
   */
  ids: string[];
  onReorder: (from: number, to: number) => void;
  children: React.ReactNode;
}

export const SortableList = React.memo(({ ids, onReorder, children }: SortableListProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    // Space lifts, arrows move, space drops — the keyboard path the Sections list offers.
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over) {
        return;
      }
      onReorder(ids.indexOf(String(active.id)), ids.indexOf(String(over.id)));
    },
    [ids, onReorder]
  );

  return (
    // `closestCenter`, not `closestCorners`: these are single vertical lists, and corner distance
    // makes the target flicker between neighbours mid-drag (the reason Sections uses it too).
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <ul className={'storefront-extras__list'}>{children}</ul>
      </SortableContext>
    </DndContext>
  );
});

SortableList.displayName = 'SortableList';
